import { useState, useEffect } from "react";
import { useRouteMatch } from "react-router-dom";
import PropTypes from "prop-types";
import { cloneDeep } from "lodash-es";
import { MLDRIVEN_ITEMS_ALGORITHM_TYPE } from "./constants";
import { getBaseSection } from "../../../../utils/path/library";
import { getUserFromCookie } from "../../../../utils/cookies/library";
import { fetchMLDrivenItemsForLayout } from "./utils";
import { SectionHeading } from "../../../../components/Heading/component";
import LayoutFactory, {
    getPlaceholderItems,
} from "../../../../components/LayoutFactory/component";
import useMounted from "../../../../hooks/useMounted";
import log from "../../../../utils/logger/library";

const MLDrivenItemsWidget = ({ params }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [mlDrivenItems, setMlDrivenItems] = useState([]);
    const match = useRouteMatch();
    const path = getBaseSection(match.url);
    const isMounted = useMounted();

    const fetchMLDrivenItems = async () => {
        try {
            setIsLoading(true);
            const contentItems = await fetchMLDrivenItemsForLayout(
                mlDrivenItemsConfig,
                path
            );
            setMlDrivenItems(contentItems);
        } catch (error) {
            setMlDrivenItems([]);
            log.error(
                `(MLDrivenItemsWidget) fetch trending items failed`,
                error
            );
        }

        setIsLoading(false);
    };

    useEffect(() => {
        if (params && isMounted) fetchMLDrivenItems();
    }, [params, isMounted]);

    if (!params) {
        return null;
    }

    const { title, description, layout, query, featureAttribution } = params;

    if (!layout || !query?.args || !query?.algorithm) return null;

    const mlDrivenItemsConfig = {
        query: cloneDeep(query),
        layout: cloneDeep(layout),
    };

    if (query.algorithm === MLDRIVEN_ITEMS_ALGORITHM_TYPE.RECOMMENDED) {
        const user = getUserFromCookie();
        //to get this to function locally, you will have to assign a value to the userId since cookies are not available in local environment
        mlDrivenItemsConfig.query.args.userId = user?.id ?? "";
    }

    if (!isLoading && !mlDrivenItems?.length) return null;

    return (
        <div
            className="mlDrivenItemsWidget"
            data-testid="mldriven-items-widget"
        >
            {title && <SectionHeading text={title} description={description} />}
            <LayoutFactory
                layout={layout}
                items={
                    isLoading
                        ? getPlaceholderItems(query.pageSize)
                        : mlDrivenItems
                }
                featureAttributionData={featureAttribution}
            />
        </div>
    );
};

export default MLDrivenItemsWidget;

MLDrivenItemsWidget.propTypes = {
    params: PropTypes.shape({
        query: PropTypes.shape({
            args: PropTypes.shape({
                trendingType: PropTypes.string,
            }),
            pageSize: PropTypes.number,
            algorithm: PropTypes.string,
        }),
        featureAttribution: PropTypes.shape({
            name: PropTypes.string,
            instance: PropTypes.string,
        }),
        title: PropTypes.string,
        description: PropTypes.string,
    }),
};
